/**
 * Smoke Detector Calculations Utility
 *
 * Provides comprehensive smoke detection and fire risk assessment functions for the IoT Edge Platform.
 * Supports smoke density analysis, fire risk levels, alarm triggers, visibility impact,
 * air quality correlation, and emergency response recommendations.
 *
 * Features:
 * - Smoke density classification (0-100%)
 * - Fire risk assessment (5 levels)
 * - Alarm trigger detection with debouncing
 * - Visibility impact calculation
 * - Air quality index correlation
 * - Statistical analysis (averages, peak times, alarm counts)
 * - Context-aware safety recommendations
 * - Emergency action generation
 * - Bilingual support (Arabic/English)
 *
 * @module smokeCalculations
 */

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Smoke density level classification
 */
export type SmokeDensityLevel = 'clear' | 'minimal' | 'light' | 'moderate' | 'heavy' | 'dense';

/**
 * Fire risk assessment level
 */
export type FireRiskLevel = 'none' | 'low' | 'moderate' | 'high' | 'critical';

/**
 * Alarm trigger status
 */
export type AlarmStatus = 'normal' | 'prealarm' | 'triggered' | 'critical';

/**
 * Smoke reading data point
 */
export interface SmokeReading {
  density: number; // Smoke density percentage (0-100%)
  time: Date;
  temperature?: number; // Optional temperature reading (°C)
  co?: number; // Optional CO reading (ppm)
}

/**
 * Smoke density thresholds
 */
export interface SmokeThresholds {
  clear: number; // <2%
  minimal: number; // 2-5%
  light: number; // 5-15%
  moderate: number; // 15-30%
  heavy: number; // 30-60%
  dense: number; // >60%
  alarmTrigger: number; // Alarm activation threshold (typically 20%)
  criticalAlarm: number; // Critical alarm threshold (typically 50%)
}

/**
 * Fire risk assessment information
 */
export interface FireRiskInfo {
  level: FireRiskLevel;
  confidence: number; // Confidence percentage (0-100%)
  factors: string[]; // Contributing factors
  temperatureCorrelation: boolean; // Temperature increasing with smoke
  coCorrelation: boolean; // CO detected with smoke
  rapidIncrease: boolean; // Density increasing rapidly
}

/**
 * Alarm information
 */
export interface AlarmInfo {
  status: AlarmStatus;
  triggered: boolean;
  triggerTime?: Date;
  duration: number; // Duration in seconds
  peakDensity: number; // Highest density during alarm
  debounceActive: boolean; // Debounce timer active
}

/**
 * Visibility impact assessment
 */
export interface VisibilityInfo {
  estimatedRange: number; // Estimated visibility range in meters
  impactLevel: 'none' | 'slight' | 'moderate' | 'severe' | 'zero'; // Impact on visibility
  evacuationDifficulty: 'easy' | 'moderate' | 'difficult' | 'extreme'; // Evacuation difficulty
}

/**
 * Smoke statistics over a time period
 */
export interface SmokeStats {
  averageDensity: number;
  peakDensity: number;
  peakDensityTime?: Date;
  minDensity: number;
  clearHours: number; // Hours with density < 2%
  concernHours: number; // Hours with density 5-30%
  dangerHours: number; // Hours with density > 30%
  alarmCount: number; // Number of alarm triggers
  averageAlarmDuration: number; // Average alarm duration in seconds
  rapidIncreaseCount: number; // Number of rapid density increases
}

/**
 * Comprehensive smoke detector status
 */
export interface SmokeStatus {
  density: number;
  densityLevel: SmokeDensityLevel;
  fireRisk: FireRiskInfo;
  alarm: AlarmInfo;
  visibility: VisibilityInfo;
  airQualityImpact: 'none' | 'minor' | 'moderate' | 'severe' | 'hazardous';
  recommendations: string[];
  emergencyActions: string[];
  stats: SmokeStats;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Standard smoke density thresholds
 */
export const SMOKE_THRESHOLDS: SmokeThresholds = {
  clear: 2,
  minimal: 5,
  light: 15,
  moderate: 30,
  heavy: 60,
  dense: 100,
  alarmTrigger: 20,
  criticalAlarm: 50,
};

/**
 * Debounce time for alarm triggering (seconds)
 */
export const ALARM_DEBOUNCE_SECONDS = 10;

/**
 * Rapid increase threshold (%/minute)
 */
export const RAPID_INCREASE_THRESHOLD = 5;

// ============================================================================
// Core Functions
// ============================================================================

/**
 * Get smoke density level classification
 */
export function getSmokeDensityLevel(density: number): SmokeDensityLevel {
  if (density < SMOKE_THRESHOLDS.clear) return 'clear';
  if (density < SMOKE_THRESHOLDS.minimal) return 'minimal';
  if (density < SMOKE_THRESHOLDS.light) return 'light';
  if (density < SMOKE_THRESHOLDS.moderate) return 'moderate';
  if (density < SMOKE_THRESHOLDS.heavy) return 'heavy';
  return 'dense';
}

/**
 * Assess fire risk based on smoke density and correlating factors
 */
export function assessFireRisk(
  density: number,
  readings: SmokeReading[],
  currentTemperature?: number,
  currentCO?: number,
): FireRiskInfo {
  const factors: string[] = [];
  let confidence = 0;

  // Base risk from density
  let level: FireRiskLevel = 'none';

  if (density < 5) {
    level = 'none';
    confidence = 95;
  } else if (density < 15) {
    level = 'low';
    confidence = 60;
    factors.push('Light smoke detected');
  } else if (density < 30) {
    level = 'moderate';
    confidence = 70;
    factors.push('Moderate smoke levels');
  } else if (density < 50) {
    level = 'high';
    confidence = 80;
    factors.push('Heavy smoke present');
  } else {
    level = 'critical';
    confidence = 90;
    factors.push('Dense smoke detected');
  }

  // Check for temperature correlation (fire indicator)
  const temperatureCorrelation = checkTemperatureCorrelation(readings, currentTemperature);
  if (temperatureCorrelation && density > 10) {
    confidence = Math.min(100, confidence + 15);
    factors.push('Temperature rising with smoke');
    if (level === 'low') level = 'moderate';
    else if (level === 'moderate') level = 'high';
    else if (level === 'high') level = 'critical';
  }

  // Check for CO correlation (combustion indicator)
  const coCorrelation = checkCOCorrelation(readings, currentCO);
  if (coCorrelation && density > 10) {
    confidence = Math.min(100, confidence + 15);
    factors.push('Carbon monoxide detected');
    if (level === 'low') level = 'moderate';
    else if (level === 'moderate') level = 'high';
    else if (level === 'high') level = 'critical';
  }

  // Check for rapid density increase (sudden fire indicator)
  const rapidIncrease = detectRapidIncrease(readings, density);
  if (rapidIncrease && density > 15) {
    confidence = Math.min(100, confidence + 20);
    factors.push('Rapid smoke increase');
    if (level === 'moderate') level = 'high';
    else if (level === 'high') level = 'critical';
  }

  return {
    level,
    confidence,
    factors: factors.length > 0 ? factors : ['No fire indicators'],
    temperatureCorrelation,
    coCorrelation,
    rapidIncrease,
  };
}

/**
 * Check if temperature is rising with smoke density
 */
function checkTemperatureCorrelation(
  readings: SmokeReading[],
  currentTemperature?: number,
): boolean {
  if (!currentTemperature || readings.length < 5) return false;

  // Get readings from last 10 minutes with temperature data
  const now = new Date();
  const recentReadings = readings
    .filter((r) => r.temperature !== undefined)
    .filter((r) => now.getTime() - r.time.getTime() < 10 * 60 * 1000)
    .sort((a, b) => a.time.getTime() - b.time.getTime());

  if (recentReadings.length < 3) return false;

  // Check if both temperature and density increased
  const firstReading = recentReadings[0];
  const tempIncrease = currentTemperature - (firstReading.temperature || 0);
  const densityIncrease = readings[readings.length - 1]?.density - firstReading.density;

  return tempIncrease > 2 && densityIncrease > 5; // >2°C and >5% density increase
}

/**
 * Check if CO is detected with smoke
 */
function checkCOCorrelation(readings: SmokeReading[], currentCO?: number): boolean {
  if (!currentCO || currentCO < 5) return false; // CO threshold 5 ppm

  // Get recent readings with CO data
  const now = new Date();
  const recentReadings = readings
    .filter((r) => r.co !== undefined && r.co > 0)
    .filter((r) => now.getTime() - r.time.getTime() < 5 * 60 * 1000);

  return recentReadings.length > 2; // CO detected in multiple readings
}

/**
 * Detect rapid smoke density increase
 */
function detectRapidIncrease(readings: SmokeReading[], currentDensity: number): boolean {
  if (readings.length < 3) return false;

  // Get readings from last 2 minutes
  const now = new Date();
  const recentReadings = readings
    .filter((r) => now.getTime() - r.time.getTime() < 2 * 60 * 1000)
    .sort((a, b) => a.time.getTime() - b.time.getTime());

  if (recentReadings.length < 2) return false;

  // Calculate rate of increase
  const firstReading = recentReadings[0];
  const timeDiff = (now.getTime() - firstReading.time.getTime()) / (60 * 1000); // minutes
  const densityDiff = currentDensity - firstReading.density;

  if (timeDiff === 0) return false;

  const increaseRate = densityDiff / timeDiff; // %/minute

  return increaseRate > RAPID_INCREASE_THRESHOLD; // >5%/minute
}

/**
 * Detect and analyze alarm status
 */
export function detectAlarm(readings: SmokeReading[], currentDensity: number): AlarmInfo {
  let status: AlarmStatus = 'normal';
  let triggered = false;
  let triggerTime: Date | undefined;
  let duration = 0;
  let peakDensity = currentDensity;
  let debounceActive = false;

  // Check current density against thresholds
  if (currentDensity >= SMOKE_THRESHOLDS.criticalAlarm) {
    status = 'critical';
    triggered = true;
  } else if (currentDensity >= SMOKE_THRESHOLDS.alarmTrigger) {
    // Check if density sustained for debounce period
    const now = new Date();
    const debounceReadings = readings
      .filter((r) => now.getTime() - r.time.getTime() < ALARM_DEBOUNCE_SECONDS * 1000)
      .filter((r) => r.density >= SMOKE_THRESHOLDS.alarmTrigger);

    if (debounceReadings.length >= 3) {
      // Sustained for 3+ readings
      status = 'triggered';
      triggered = true;
    } else {
      status = 'prealarm';
      debounceActive = true;
    }
  } else if (currentDensity >= SMOKE_THRESHOLDS.light) {
    status = 'prealarm';
  }

  // If alarm triggered, find trigger time and duration
  if (triggered) {
    const alarmReadings = readings
      .filter((r) => r.density >= SMOKE_THRESHOLDS.alarmTrigger)
      .sort((a, b) => a.time.getTime() - b.time.getTime());

    if (alarmReadings.length > 0) {
      triggerTime = alarmReadings[0].time;
      duration = Math.floor((new Date().getTime() - triggerTime.getTime()) / 1000);
      peakDensity = Math.max(...alarmReadings.map((r) => r.density));
    }
  }

  return {
    status,
    triggered,
    triggerTime,
    duration,
    peakDensity,
    debounceActive,
  };
}

/**
 * Calculate visibility impact from smoke density
 */
export function calculateVisibility(density: number): VisibilityInfo {
  let estimatedRange: number;
  let impactLevel: VisibilityInfo['impactLevel'];
  let evacuationDifficulty: VisibilityInfo['evacuationDifficulty'];

  // Visibility estimation based on smoke density
  // Formula: Range (m) = 1000 / (density%)^1.5
  if (density < 2) {
    estimatedRange = 100; // Clear visibility >100m
    impactLevel = 'none';
    evacuationDifficulty = 'easy';
  } else if (density < 10) {
    estimatedRange = Math.max(30, 100 - density * 7);
    impactLevel = 'slight';
    evacuationDifficulty = 'easy';
  } else if (density < 25) {
    estimatedRange = Math.max(10, 30 - (density - 10) * 1.3);
    impactLevel = 'moderate';
    evacuationDifficulty = 'moderate';
  } else if (density < 50) {
    estimatedRange = Math.max(2, 10 - (density - 25) * 0.3);
    impactLevel = 'severe';
    evacuationDifficulty = 'difficult';
  } else {
    estimatedRange = density < 80 ? 1 : 0;
    impactLevel = density < 80 ? 'severe' : 'zero';
    evacuationDifficulty = 'extreme';
  }

  return {
    estimatedRange: Math.round(estimatedRange * 10) / 10,
    impactLevel,
    evacuationDifficulty,
  };
}

/**
 * Calculate average smoke density over a time period
 */
export function calculateAverageDensity(
  readings: SmokeReading[],
  startTime: Date,
  endTime: Date,
): number {
  const periodReadings = readings.filter((r) => r.time >= startTime && r.time <= endTime);

  if (periodReadings.length === 0) return 0;

  const sum = periodReadings.reduce((acc, r) => acc + r.density, 0);
  return Math.round((sum / periodReadings.length) * 10) / 10;
}

/**
 * Get comprehensive smoke statistics for a time period
 */
export function getSmokeStatistics(
  readings: SmokeReading[],
  startTime: Date,
  endTime: Date,
): SmokeStats {
  const periodReadings = readings
    .filter((r) => r.time >= startTime && r.time <= endTime)
    .sort((a, b) => a.time.getTime() - b.time.getTime());

  if (periodReadings.length === 0) {
    return {
      averageDensity: 0,
      peakDensity: 0,
      minDensity: 0,
      clearHours: 0,
      concernHours: 0,
      dangerHours: 0,
      alarmCount: 0,
      averageAlarmDuration: 0,
      rapidIncreaseCount: 0,
    };
  }

  // Calculate basic statistics
  const densities = periodReadings.map((r) => r.density);
  const averageDensity =
    Math.round((densities.reduce((a, b) => a + b, 0) / densities.length) * 10) / 10;
  const peakDensity = Math.max(...densities);
  const minDensity = Math.min(...densities);
  const peakReading = periodReadings.find((r) => r.density === peakDensity);

  // Calculate hours in different density ranges
  const totalHours = (endTime.getTime() - startTime.getTime()) / (60 * 60 * 1000);
  let clearHours = 0;
  let concernHours = 0;
  let dangerHours = 0;

  // Group readings by hour and classify
  const hourlyDensities = new Map<number, number[]>();
  periodReadings.forEach((r) => {
    const hour = Math.floor(r.time.getTime() / (60 * 60 * 1000));
    if (!hourlyDensities.has(hour)) {
      hourlyDensities.set(hour, []);
    }
    hourlyDensities.get(hour)!.push(r.density);
  });

  hourlyDensities.forEach((densities) => {
    const avgDensity = densities.reduce((a, b) => a + b, 0) / densities.length;
    if (avgDensity < SMOKE_THRESHOLDS.clear) {
      clearHours++;
    } else if (avgDensity < SMOKE_THRESHOLDS.moderate) {
      concernHours++;
    } else {
      dangerHours++;
    }
  });

  // Detect alarm events
  let alarmCount = 0;
  let totalAlarmDuration = 0;
  let inAlarm = false;
  let alarmStart: Date | undefined;

  periodReadings.forEach((reading, index) => {
    if (reading.density >= SMOKE_THRESHOLDS.alarmTrigger && !inAlarm) {
      inAlarm = true;
      alarmStart = reading.time;
      alarmCount++;
    } else if (reading.density < SMOKE_THRESHOLDS.light && inAlarm) {
      inAlarm = false;
      if (alarmStart) {
        totalAlarmDuration += (reading.time.getTime() - alarmStart.getTime()) / 1000;
      }
    }
  });

  // If still in alarm at end of period
  if (inAlarm && alarmStart) {
    totalAlarmDuration += (endTime.getTime() - alarmStart.getTime()) / 1000;
  }

  const averageAlarmDuration = alarmCount > 0 ? Math.round(totalAlarmDuration / alarmCount) : 0;

  // Count rapid increases
  let rapidIncreaseCount = 0;
  for (let i = 5; i < periodReadings.length; i++) {
    const recent = periodReadings.slice(i - 5, i);
    if (detectRapidIncrease(recent, periodReadings[i].density)) {
      rapidIncreaseCount++;
    }
  }

  return {
    averageDensity,
    peakDensity,
    peakDensityTime: peakReading?.time,
    minDensity,
    clearHours: Math.round(clearHours * 10) / 10,
    concernHours: Math.round(concernHours * 10) / 10,
    dangerHours: Math.round(dangerHours * 10) / 10,
    alarmCount,
    averageAlarmDuration,
    rapidIncreaseCount,
  };
}

/**
 * Generate context-aware safety recommendations
 */
export function generateRecommendations(
  density: number,
  densityLevel: SmokeDensityLevel,
  fireRisk: FireRiskInfo,
  alarm: AlarmInfo,
  visibility: VisibilityInfo,
  lang: 'en' | 'ar' = 'en',
): string[] {
  const recommendations: string[] = [];

  if (lang === 'ar') {
    // Arabic recommendations
    if (densityLevel === 'clear' || densityLevel === 'minimal') {
      recommendations.push('مستوى الدخان طبيعي - استمر في المراقبة');
      recommendations.push('تأكد من عمل كاشف الدخان بشكل صحيح');
    } else if (densityLevel === 'light') {
      recommendations.push('دخان خفيف مكتشف - تحقق من المصدر');
      recommendations.push('افتح النوافذ لتحسين التهوية');
      if (fireRisk.level === 'moderate' || fireRisk.level === 'high') {
        recommendations.push('راقب الوضع عن كثب - قد تكون هناك بداية حريق');
      }
    } else if (densityLevel === 'moderate') {
      recommendations.push('مستوى دخان متوسط - حدد المصدر فوراً');
      recommendations.push('استعد للإخلاء في حالة زيادة الدخان');
      if (visibility.evacuationDifficulty === 'moderate') {
        recommendations.push('الرؤية متأثرة - استخدم الإضاءة الطارئة');
      }
    } else if (densityLevel === 'heavy' || densityLevel === 'dense') {
      recommendations.push('خطر: دخان كثيف - اخلِ المنطقة فوراً');
      recommendations.push('اتصل بالطوارئ (998) فوراً');
      if (visibility.impactLevel === 'severe' || visibility.impactLevel === 'zero') {
        recommendations.push('الرؤية شبه معدومة - ابقَ منخفضاً للأرض');
      }
    }

    // Fire risk specific
    if (fireRisk.level === 'high' || fireRisk.level === 'critical') {
      recommendations.push('خطر حريق مرتفع - لا تتردد في الإخلاء');
      if (fireRisk.temperatureCorrelation) {
        recommendations.push('درجة الحرارة مرتفعة - قد يكون هناك حريق نشط');
      }
      if (fireRisk.rapidIncrease) {
        recommendations.push('انتشار سريع - تصرف بسرعة');
      }
    }

    // Alarm specific
    if (alarm.triggered) {
      recommendations.push('الإنذار مفعّل - اتبع خطة الإخلاء');
    }
  } else {
    // English recommendations
    if (densityLevel === 'clear' || densityLevel === 'minimal') {
      recommendations.push('Smoke level normal - continue monitoring');
      recommendations.push('Verify smoke detector is functioning properly');
    } else if (densityLevel === 'light') {
      recommendations.push('Light smoke detected - investigate source');
      recommendations.push('Open windows to improve ventilation');
      if (fireRisk.level === 'moderate' || fireRisk.level === 'high') {
        recommendations.push('Monitor situation closely - possible fire start');
      }
    } else if (densityLevel === 'moderate') {
      recommendations.push('Moderate smoke level - locate source immediately');
      recommendations.push('Prepare for evacuation if smoke increases');
      if (visibility.evacuationDifficulty === 'moderate') {
        recommendations.push('Visibility affected - use emergency lighting');
      }
    } else if (densityLevel === 'heavy' || densityLevel === 'dense') {
      recommendations.push('Danger: Heavy smoke - evacuate area immediately');
      recommendations.push('Call emergency services (998) immediately');
      if (visibility.impactLevel === 'severe' || visibility.impactLevel === 'zero') {
        recommendations.push('Near-zero visibility - stay low to ground');
      }
    }

    // Fire risk specific
    if (fireRisk.level === 'high' || fireRisk.level === 'critical') {
      recommendations.push('High fire risk - do not delay evacuation');
      if (fireRisk.temperatureCorrelation) {
        recommendations.push('Temperature rising - active fire may be present');
      }
      if (fireRisk.rapidIncrease) {
        recommendations.push('Rapid spread detected - act quickly');
      }
    }

    // Alarm specific
    if (alarm.triggered) {
      recommendations.push('Alarm triggered - follow evacuation plan');
    }
  }

  return recommendations.slice(0, 3); // Return top 3 recommendations
}

/**
 * Generate emergency action steps for critical situations
 */
export function generateEmergencyActions(
  densityLevel: SmokeDensityLevel,
  fireRisk: FireRiskInfo,
  alarm: AlarmInfo,
  visibility: VisibilityInfo,
  lang: 'en' | 'ar' = 'en',
): string[] {
  // Only generate emergency actions for dangerous situations
  if (densityLevel !== 'heavy' && densityLevel !== 'dense' && fireRisk.level !== 'critical') {
    return [];
  }

  const actions: string[] = [];

  if (lang === 'ar') {
    actions.push('اخلِ المبنى فوراً عبر أقرب مخرج آمن');
    actions.push('اتصل بالطوارئ (998) فوراً');

    if (visibility.impactLevel === 'severe' || visibility.impactLevel === 'zero') {
      actions.push('ابقَ منخفضاً للأرض - الهواء أنظف بالقرب من الأرضية');
      actions.push('استخدم قطعة قماش مبللة على الأنف والفم');
    }

    actions.push('لا تستخدم المصاعد - استخدم السلالم فقط');
    actions.push('أغلق الأبواب خلفك لإبطاء انتشار الدخان');
    actions.push('اجتمع في نقطة التجمع المحددة');

    if (fireRisk.temperatureCorrelation) {
      actions.push('لا تلمس الأبواب - قد تكون ساخنة');
    }

    actions.push('لا تعد للمبنى حتى تسمح السلطات بذلك');
  } else {
    actions.push('Evacuate building immediately via nearest safe exit');
    actions.push('Call emergency services (998) immediately');

    if (visibility.impactLevel === 'severe' || visibility.impactLevel === 'zero') {
      actions.push('Stay low to ground - air is clearer near floor');
      actions.push('Use wet cloth over nose and mouth');
    }

    actions.push('Do not use elevators - use stairs only');
    actions.push('Close doors behind you to slow smoke spread');
    actions.push('Meet at designated assembly point');

    if (fireRisk.temperatureCorrelation) {
      actions.push('Do not touch doors - may be hot');
    }

    actions.push('Do not re-enter building until authorities allow');
  }

  return actions;
}

/**
 * Assess air quality impact from smoke
 */
export function assessAirQualityImpact(density: number): SmokeStatus['airQualityImpact'] {
  if (density < 5) return 'none';
  if (density < 15) return 'minor';
  if (density < 30) return 'moderate';
  if (density < 50) return 'severe';
  return 'hazardous';
}

/**
 * Get comprehensive smoke detector status
 */
export function getSmokeStatus(
  readings: SmokeReading[],
  currentDensity: number,
  currentTemperature?: number,
  currentCO?: number,
  lang: 'en' | 'ar' = 'en',
): SmokeStatus {
  const densityLevel = getSmokeDensityLevel(currentDensity);
  const fireRisk = assessFireRisk(currentDensity, readings, currentTemperature, currentCO);
  const alarm = detectAlarm(readings, currentDensity);
  const visibility = calculateVisibility(currentDensity);
  const airQualityImpact = assessAirQualityImpact(currentDensity);

  // Get yesterday's stats for comparison
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);
  const yesterdayEnd = new Date(yesterday);
  yesterdayEnd.setHours(23, 59, 59, 999);

  const stats = getSmokeStatistics(readings, yesterday, yesterdayEnd);

  const recommendations = generateRecommendations(
    currentDensity,
    densityLevel,
    fireRisk,
    alarm,
    visibility,
    lang,
  );

  const emergencyActions = generateEmergencyActions(
    densityLevel,
    fireRisk,
    alarm,
    visibility,
    lang,
  );

  return {
    density: currentDensity,
    densityLevel,
    fireRisk,
    alarm,
    visibility,
    airQualityImpact,
    recommendations,
    emergencyActions,
    stats,
  };
}

// ============================================================================
// Formatting Functions
// ============================================================================

/**
 * Format smoke density with unit
 */
export function formatDensity(density: number, lang: 'en' | 'ar' = 'en'): string {
  const formatted = density.toFixed(1);
  return lang === 'ar' ? `${formatted}٪` : `${formatted}%`;
}

/**
 * Format smoke density level
 */
export function formatDensityLevel(level: SmokeDensityLevel, lang: 'en' | 'ar' = 'en'): string {
  const labels: Record<SmokeDensityLevel, { en: string; ar: string }> = {
    clear: { en: 'Clear', ar: 'صافي' },
    minimal: { en: 'Minimal', ar: 'بسيط' },
    light: { en: 'Light', ar: 'خفيف' },
    moderate: { en: 'Moderate', ar: 'متوسط' },
    heavy: { en: 'Heavy', ar: 'كثيف' },
    dense: { en: 'Dense', ar: 'كثيف جداً' },
  };
  return labels[level][lang];
}

/**
 * Format fire risk level
 */
export function formatFireRisk(level: FireRiskLevel, lang: 'en' | 'ar' = 'en'): string {
  const labels: Record<FireRiskLevel, { en: string; ar: string }> = {
    none: { en: 'None', ar: 'لا يوجد' },
    low: { en: 'Low', ar: 'منخفض' },
    moderate: { en: 'Moderate', ar: 'متوسط' },
    high: { en: 'High', ar: 'مرتفع' },
    critical: { en: 'Critical', ar: 'حرج' },
  };
  return labels[level][lang];
}

/**
 * Format alarm status
 */
export function formatAlarmStatus(status: AlarmStatus, lang: 'en' | 'ar' = 'en'): string {
  const labels: Record<AlarmStatus, { en: string; ar: string }> = {
    normal: { en: 'Normal', ar: 'طبيعي' },
    prealarm: { en: 'Pre-Alarm', ar: 'ما قبل الإنذار' },
    triggered: { en: 'Triggered', ar: 'مفعّل' },
    critical: { en: 'Critical Alarm', ar: 'إنذار حرج' },
  };
  return labels[status][lang];
}

/**
 * Format visibility impact level
 */
export function formatVisibilityImpact(
  level: VisibilityInfo['impactLevel'],
  lang: 'en' | 'ar' = 'en',
): string {
  const labels: Record<VisibilityInfo['impactLevel'], { en: string; ar: string }> = {
    none: { en: 'Clear', ar: 'واضح' },
    slight: { en: 'Slight', ar: 'طفيف' },
    moderate: { en: 'Moderate', ar: 'متوسط' },
    severe: { en: 'Severe', ar: 'شديد' },
    zero: { en: 'Zero Visibility', ar: 'معدوم' },
  };
  return labels[level][lang];
}

/**
 * Format evacuation difficulty
 */
export function formatEvacuationDifficulty(
  difficulty: VisibilityInfo['evacuationDifficulty'],
  lang: 'en' | 'ar' = 'en',
): string {
  const labels: Record<VisibilityInfo['evacuationDifficulty'], { en: string; ar: string }> = {
    easy: { en: 'Easy', ar: 'سهل' },
    moderate: { en: 'Moderate', ar: 'متوسط' },
    difficult: { en: 'Difficult', ar: 'صعب' },
    extreme: { en: 'Extreme', ar: 'شديد الصعوبة' },
  };
  return labels[difficulty][lang];
}

/**
 * Format visibility range
 */
export function formatVisibilityRange(range: number, lang: 'en' | 'ar' = 'en'): string {
  if (range === 0) {
    return lang === 'ar' ? 'معدوم' : 'Zero';
  }
  if (range < 1) {
    return lang === 'ar' ? `<١ متر` : `<1m`;
  }
  if (range >= 100) {
    return lang === 'ar' ? `>١٠٠ متر` : `>100m`;
  }
  return lang === 'ar' ? `${range.toFixed(1)} متر` : `${range.toFixed(1)}m`;
}

/**
 * Format air quality impact
 */
export function formatAirQualityImpact(
  impact: SmokeStatus['airQualityImpact'],
  lang: 'en' | 'ar' = 'en',
): string {
  const labels: Record<SmokeStatus['airQualityImpact'], { en: string; ar: string }> = {
    none: { en: 'No Impact', ar: 'لا تأثير' },
    minor: { en: 'Minor Impact', ar: 'تأثير طفيف' },
    moderate: { en: 'Moderate Impact', ar: 'تأثير متوسط' },
    severe: { en: 'Severe Impact', ar: 'تأثير شديد' },
    hazardous: { en: 'Hazardous', ar: 'خطير' },
  };
  return labels[impact][lang];
}

/**
 * Format duration in human-readable format
 */
export function formatDuration(seconds: number, lang: 'en' | 'ar' = 'en'): string {
  if (seconds < 60) {
    return lang === 'ar' ? `${seconds} ثانية` : `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes < 60) {
    if (remainingSeconds === 0) {
      return lang === 'ar' ? `${minutes} دقيقة` : `${minutes}m`;
    }
    return lang === 'ar'
      ? `${minutes} د ${remainingSeconds} ث`
      : `${minutes}m ${remainingSeconds}s`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return lang === 'ar' ? `${hours} ساعة` : `${hours}h`;
  }
  return lang === 'ar' ? `${hours} س ${remainingMinutes} د` : `${hours}h ${remainingMinutes}m`;
}

// ============================================================================
// Color Functions
// ============================================================================

/**
 * Get color for smoke density level
 */
export function getDensityLevelColor(level: SmokeDensityLevel): string {
  const colors: Record<SmokeDensityLevel, string> = {
    clear: '#10b981', // green
    minimal: '#84cc16', // lime
    light: '#eab308', // yellow
    moderate: '#f59e0b', // orange
    heavy: '#ef4444', // red
    dense: '#7f1d1d', // dark red
  };
  return colors[level];
}

/**
 * Get color for fire risk level
 */
export function getFireRiskColor(level: FireRiskLevel): string {
  const colors: Record<FireRiskLevel, string> = {
    none: '#10b981', // green
    low: '#84cc16', // lime
    moderate: '#f59e0b', // orange
    high: '#ef4444', // red
    critical: '#7f1d1d', // dark red
  };
  return colors[level];
}

/**
 * Get color for alarm status
 */
export function getAlarmStatusColor(status: AlarmStatus): string {
  const colors: Record<AlarmStatus, string> = {
    normal: '#10b981', // green
    prealarm: '#eab308', // yellow
    triggered: '#ef4444', // red
    critical: '#7f1d1d', // dark red
  };
  return colors[status];
}
